import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import FilterListOffIcon from '@mui/icons-material/FilterListOff';
import LinkIcon from '@mui/icons-material/Link';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useSearchParams } from 'react-router-dom';
import client from '../../api/client';
import DateInputField from '../../components/DateInputField';

const ACTION_OPTIONS = [
  { value: '', label: 'Alle Aktionen' },
  { value: 'INSERT', label: 'INSERT' },
  { value: 'CREATE', label: 'CREATE' },
  { value: 'UPDATE', label: 'UPDATE' },
  { value: 'DELETE', label: 'DELETE' },
  { value: 'PDF_EXPORT_CREATED', label: 'PDF_EXPORT_CREATED' },
  { value: 'PDF_EXPORT_COMPLETED', label: 'PDF_EXPORT_COMPLETED' },
  { value: 'PDF_EXPORT_COMPLETED_PUBLIC', label: 'PDF_EXPORT_COMPLETED_PUBLIC' },
];

const TABLE_OPTIONS = [
  { value: '', label: 'Alle Bereiche' },
  { value: 'training_hours', label: 'Trainingsstunden' },
  { value: 'users', label: 'Benutzer' },
  { value: 'user_roles', label: 'Benutzerrollen' },
  { value: 'entry_change_requests', label: 'Aenderungsanfragen' },
  { value: 'pdf_exports', label: 'PDF-Exporte' },
];

const LIMIT = 50;

// ── Helper functions ──────────────────────────────────────────────────────────

function parseJsonValue(rawValue) {
  if (!rawValue) return null;
  try {
    return JSON.parse(rawValue);
  } catch {
    return rawValue;
  }
}

function formatPrimitive(value) {
  if (value === null || typeof value === 'undefined') return '-';
  if (typeof value === 'boolean') return value ? 'Ja' : 'Nein';
  return String(value);
}

function renderChangeDetails(log) {
  const oldObj = parseJsonValue(log.old_value);
  const newObj = parseJsonValue(log.new_value);

  if (
    oldObj
    && newObj
    && typeof oldObj === 'object'
    && typeof newObj === 'object'
    && !Array.isArray(oldObj)
    && !Array.isArray(newObj)
  ) {
    const keys = Array.from(new Set([...Object.keys(oldObj), ...Object.keys(newObj)]));
    const changedRows = keys
      .filter((key) => JSON.stringify(oldObj[key]) !== JSON.stringify(newObj[key]))
      .map((key) => `${key}: ${formatPrimitive(oldObj[key])} -> ${formatPrimitive(newObj[key])}`);

    if (changedRows.length > 0) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
          {changedRows.map((row) => (
            <Typography key={row} variant="caption" sx={{ fontFamily: 'monospace' }}>
              {row}
            </Typography>
          ))}
        </Box>
      );
    }
  }

  return (
    <Box
      component="pre"
      sx={{ m: 0, fontSize: '0.7rem', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}
    >
      {JSON.stringify({ old: oldObj, new: newObj }, null, 2)}
    </Box>
  );
}

function buildFallbackEventText(log) {
  const actor = log.actor_name || log.modified_by || 'Unbekannt';
  const action = log.action_label || log.action || 'Aktion';
  const entity = log.entity_label || log.table_name || 'Datensatz';
  if (log.subject_name) {
    return `${actor} hat bei ${log.subject_name} ${entity} ${action.toLowerCase()}.`;
  }
  return `${actor} hat ${entity} ${action.toLowerCase()}.`;
}

/**
 * Maps an action string to a MUI Chip color prop value.
 */
function getActionColor(action) {
  switch (action) {
    case 'INSERT':
    case 'CREATE':
      return 'success';
    case 'UPDATE':
      return 'primary';
    case 'DELETE':
      return 'error';
    case 'PDF_EXPORT_CREATED':
    case 'PDF_EXPORT_COMPLETED':
    case 'PDF_EXPORT_COMPLETED_PUBLIC':
      return 'info';
    default:
      return 'default';
  }
}

// ── Expandable details row ────────────────────────────────────────────────────

function DetailsCell({ log }) {
  const [open, setOpen] = useState(false);
  const hasDetails = Boolean(log.old_value || log.new_value);

  if (!hasDetails) return <TableCell>-</TableCell>;

  return (
    <TableCell>
      <IconButton
        size="small"
        onClick={() => setOpen((prev) => !prev)}
        aria-label={open ? 'Details ausblenden' : 'Details anzeigen'}
      >
        {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
      </IconButton>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <Box sx={{ mt: 0.5 }}>
          {renderChangeDetails(log)}
        </Box>
      </Collapse>
    </TableCell>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

function AuditLogPage({ refreshKey }) {
  const [searchParams, setSearchParams] = useSearchParams();

  const [logs, setLogs] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [total, setTotal] = useState(0);
  const [copyState, setCopyState] = useState('');
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  const [page, setPage] = useState(() => {
    const parsed = Number.parseInt(searchParams.get('page') || '0', 10);
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
  });

  const [filters, setFilters] = useState(() => ({
    actorUserId: searchParams.get('actorUserId') || '',
    subjectUserId: searchParams.get('subjectUserId') || '',
    action: searchParams.get('action') || '',
    tableName: searchParams.get('tableName') || '',
    from: searchParams.get('from') || '',
    to: searchParams.get('to') || '',
    q: searchParams.get('q') || '',
  }));

  // ── Data loading ────────────────────────────────────────────────────────────

  const loadUsers = useCallback(async () => {
    try {
      const response = await client.get('/api/admin/users');
      setUsers(Array.isArray(response.data?.users) ? response.data.users : []);
    } catch (loadUsersError) {
      console.error('Load users error:', loadUsersError);
    }
  }, []);

  const loadLogs = useCallback(async () => {
    try {
      setLoading(true);
      const params = { limit: LIMIT, offset: page * LIMIT };

      if (filters.actorUserId) params.actorUserId = filters.actorUserId;
      if (filters.subjectUserId) params.subjectUserId = filters.subjectUserId;
      if (filters.action) params.action = filters.action;
      if (filters.tableName) params.tableName = filters.tableName;
      if (filters.from) params.from = filters.from;
      if (filters.to) params.to = filters.to;
      if (filters.q.trim()) params.q = filters.q.trim();

      const response = await client.get('/api/admin/audit-log', { params });
      setLogs(response.data.logs);
      setTotal(response.data?.pagination?.total || 0);
      setError('');
    } catch (loadError) {
      setError('Fehler beim Laden des Audit Logs');
      console.error('Load error:', loadError);
    } finally {
      setLoading(false);
    }
  }, [
    filters.action,
    filters.actorUserId,
    filters.from,
    filters.q,
    filters.subjectUserId,
    filters.tableName,
    filters.to,
    page,
  ]);

  useEffect(() => { loadUsers(); }, [loadUsers]);
  useEffect(() => { loadLogs(); }, [loadLogs, refreshKey]);

  // ── URL sync ────────────────────────────────────────────────────────────────

  useEffect(() => {
    const params = new URLSearchParams();

    if (filters.actorUserId) params.set('actorUserId', filters.actorUserId);
    if (filters.subjectUserId) params.set('subjectUserId', filters.subjectUserId);
    if (filters.action) params.set('action', filters.action);
    if (filters.tableName) params.set('tableName', filters.tableName);
    if (filters.from) params.set('from', filters.from);
    if (filters.to) params.set('to', filters.to);
    if (filters.q.trim()) params.set('q', filters.q.trim());
    if (page > 0) params.set('page', String(page));

    const nextQuery = params.toString();
    const currentQuery = searchParams.toString();
    if (nextQuery !== currentQuery) {
      setSearchParams(params, { replace: true });
    }
  }, [filters, page, searchParams, setSearchParams]);

  // ── Responsive filter visibility ────────────────────────────────────────────

  useEffect(() => {
    const syncMobileFilterState = () => {
      if (window.innerWidth > 760) {
        setMobileFiltersOpen(true);
      }
    };

    syncMobileFilterState();
    window.addEventListener('resize', syncMobileFilterState);
    return () => window.removeEventListener('resize', syncMobileFilterState);
  }, []);

  // ── Filter helpers ──────────────────────────────────────────────────────────

  const updateFilter = (key, value) => {
    setPage(0);
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setPage(0);
    setFilters({ actorUserId: '', subjectUserId: '', action: '', tableName: '', from: '', to: '', q: '' });
    setSearchParams(new URLSearchParams(), { replace: true });
  };

  const copyCurrentLink = async () => {
    const currentUrl = window.location.href;
    try {
      await navigator.clipboard.writeText(currentUrl);
      setCopyState('Link kopiert');
    } catch (copyError) {
      console.error('Copy link error:', copyError);
      setCopyState('Kopieren nicht moeglich');
    }
    window.setTimeout(() => setCopyState(''), 2000);
  };

  // ── Derived values ──────────────────────────────────────────────────────────

  const totalPages = Math.ceil(total / LIMIT);

  const actionCluster = useMemo(() => {
    const counts = logs.reduce((acc, item) => {
      const key = String(item.action || 'OTHER').toUpperCase();
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});

    const priority = ['UPDATE', 'DELETE', 'INSERT', 'CREATE'];
    const keys = Object.keys(counts).sort((a, b) => {
      const pa = priority.indexOf(a);
      const pb = priority.indexOf(b);
      if (pa !== -1 || pb !== -1) {
        return (pa === -1 ? 99 : pa) - (pb === -1 ? 99 : pb);
      }
      return counts[b] - counts[a];
    });

    return keys.slice(0, 4).map((key) => ({
      key,
      count: counts[key],
      color: getActionColor(key),
    }));
  }, [logs]);

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ px: { xs: 1.5, sm: 2, md: 5 }, py: { xs: 2, md: 2.5 } }}>
      {/* Page heading + header actions */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ sm: 'center' }}
        justifyContent="space-between"
        spacing={1}
        sx={{ mb: 2 }}
      >
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
          Audit Log
        </Typography>

        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <Button
            size="small"
            variant="outlined"
            startIcon={mobileFiltersOpen ? <FilterListOffIcon /> : <FilterListIcon />}
            onClick={() => setMobileFiltersOpen((prev) => !prev)}
          >
            {mobileFiltersOpen ? 'Filter ausblenden' : 'Filter einblenden'}
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<LinkIcon />}
            onClick={copyCurrentLink}
          >
            Link kopieren
          </Button>
          {copyState && (
            <Typography variant="caption" color="success.main" sx={{ fontWeight: 600 }}>
              {copyState}
            </Typography>
          )}
        </Stack>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Filter card */}
      <Collapse in={mobileFiltersOpen} timeout="auto">
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent>
            <Grid container spacing={2}>
              {/* Row 1: Dropdowns */}
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel id="actor-label">Akteur</InputLabel>
                  <Select
                    labelId="actor-label"
                    label="Akteur"
                    value={filters.actorUserId}
                    onChange={(e) => updateFilter('actorUserId', e.target.value)}
                  >
                    <MenuItem value="">Alle</MenuItem>
                    {users.map((user) => (
                      <MenuItem key={user.id} value={user.id}>{user.full_name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel id="subject-label">Betroffener Nutzer</InputLabel>
                  <Select
                    labelId="subject-label"
                    label="Betroffener Nutzer"
                    value={filters.subjectUserId}
                    onChange={(e) => updateFilter('subjectUserId', e.target.value)}
                  >
                    <MenuItem value="">Alle</MenuItem>
                    {users.map((user) => (
                      <MenuItem key={user.id} value={user.id}>{user.full_name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel id="action-label">Aktion</InputLabel>
                  <Select
                    labelId="action-label"
                    label="Aktion"
                    value={filters.action}
                    onChange={(e) => updateFilter('action', e.target.value)}
                  >
                    {ACTION_OPTIONS.map((option) => (
                      <MenuItem key={option.value || 'all'} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel id="table-label">Bereich</InputLabel>
                  <Select
                    labelId="table-label"
                    label="Bereich"
                    value={filters.tableName}
                    onChange={(e) => updateFilter('tableName', e.target.value)}
                  >
                    {TABLE_OPTIONS.map((option) => (
                      <MenuItem key={option.value || 'all-tables'} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Row 2: Date range, search, reset */}
              <Grid item xs={6} sm={3}>
                <DateInputField
                  fullWidth
                  size="small"
                  label="Von"
                  value={filters.from}
                  onChange={(value) => updateFilter('from', value)}
                />
              </Grid>

              <Grid item xs={6} sm={3}>
                <DateInputField
                  fullWidth
                  size="small"
                  label="Bis"
                  value={filters.to}
                  onChange={(value) => updateFilter('to', value)}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Suche"
                  placeholder="Grund, Aktion, Tabelle..."
                  value={filters.q}
                  onChange={(e) => updateFilter('q', e.target.value)}
                />
              </Grid>

              <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button variant="text" color="inherit" onClick={clearFilters}>
                  Filter zurücksetzen
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Collapse>

      {/* Action cluster badges */}
      {!loading && actionCluster.length > 0 && (
        <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
          {actionCluster.map((item) => (
            <Chip
              key={item.key}
              label={`${item.key}: ${item.count}`}
              color={item.color}
              size="small"
            />
          ))}
        </Stack>
      )}

      {/* Table card */}
      <Card variant="outlined">
        <TableContainer component={Paper} elevation={0}>
          <Table size="small" aria-label="Audit Log Tabelle">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Zeitstempel</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Ereignis</TableCell>
                <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Akteur</TableCell>
                <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Betroffener</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Aktion</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Bereich</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Grund</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Details</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    Laedt...
                  </TableCell>
                </TableRow>
              )}

              {!loading && logs.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    Keine Eintraege fuer die aktuellen Filter gefunden.
                  </TableCell>
                </TableRow>
              )}

              {!loading && logs.map((log) => (
                <TableRow key={log.id} hover>
                  <TableCell sx={{ whiteSpace: 'nowrap', fontSize: '0.78rem' }}>
                    {new Date(log.created_at).toLocaleString('de-DE')}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 280, fontSize: '0.82rem' }}>
                    {log.event_text || buildFallbackEventText(log)}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap', fontSize: '0.82rem' }}>
                    {log.actor_name || log.modified_by || '-'}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap', fontSize: '0.82rem' }}>
                    {log.subject_name || '-'}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={log.action}
                      color={getActionColor(log.action)}
                      size="small"
                      sx={{ fontSize: '0.7rem' }}
                    />
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.82rem' }}>
                    {log.entity_label || log.table_name}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 160, fontSize: '0.82rem' }}>
                    {log.reason || '-'}
                  </TableCell>
                  <DetailsCell log={log} />
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Pagination
            count={totalPages}
            page={page + 1}
            onChange={(_, value) => setPage(value - 1)}
            color="primary"
            shape="rounded"
          />
        </Box>
      )}
    </Box>
  );
}

export default AuditLogPage;
